import { type FC } from 'react';

import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';

import { appConfig } from '@app/config/appConfig';
import { LoadingPageSpinner } from '@shared/overlays';
import { GridOverlay } from '@shared/overlays/error-overlays/GridOverlay';
import { oiTheme } from '@shared/theme/theme-ag-grid';
import { cn } from '@shared/utils';

import { AG_GRID_CONFIG } from '../utils/constants';

export interface AgGridClientSideProps extends AgGridReactProps {
  hideDisabledContextMenuActions?: boolean;
  loadingText?: string;
  noRowsText?: string;
  errorText?: string;
  'data-testid'?: string;
  defaultColDef?: AgGridReactProps['defaultColDef'];
  isError?: boolean;
  refetch?: () => void;
}

const COMMON_DEFAULT_COL_DEF: AgGridReactProps['defaultColDef'] = {
  wrapHeaderText: true,
  autoHeaderHeight: true,
  suppressHeaderMenuButton: true,
  resizable: false,
  cellStyle: {
    display: 'flex',
    alignItems: 'center',
    padding: AG_GRID_CONFIG.DEFAULT_CELL_PADDING,
  },
};

export const ClientSideTableWrapper: FC<AgGridClientSideProps> = (props) => {
  const {
    columnDefs,
    rowData,
    hideDisabledContextMenuActions,
    loadingText,
    noRowsText,
    errorText,
    'data-testid': testId,
    defaultColDef,
    pagination = false,
    isError,
    refetch,
    ...rest
  } = props;

  const defaultColDefWithCommon = { ...defaultColDef, ...COMMON_DEFAULT_COL_DEF };

  return (
    <div className="flex flex-1 min-h-0">
      <div
        className={cn(
          'flex flex-col flex-1 min-h-0 w-full',
          hideDisabledContextMenuActions && 'hide-disabled-context-menus',
        )}
        data-testid={testId}
      >
        <AgGridReact
          {...rest}
          theme={oiTheme}
          rowData={rowData ?? []}
          rowModelType="clientSide"
          columnDefs={columnDefs ?? []}
          defaultColDef={defaultColDefWithCommon}
          noRowsOverlayComponent={GridOverlay}
          noRowsOverlayComponentParams={{ message: noRowsText }}
          loadingOverlayComponent={LoadingPageSpinner}
          loadingOverlayComponentParams={{ message: loadingText }}
          pagination={pagination}
          suppressScrollOnNewData
          rowBuffer={AG_GRID_CONFIG.CSRM_ROW_BUFFER}
          gridOptions={{ styleNonce: appConfig.cspNonce }}
          enableCellExpressions={false}
          context={{
            isError,
            errorMessage: errorText,
            noRowsMessage: noRowsText,
            refetch,
          }}
        />
      </div>
    </div>
  );
};
